import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowLeft, ChevronRight, Loader2 } from "lucide-react";

const SITE = "https://spa-report-hub.emergent.host";

const SEVERITY_STYLES = {
  critical: { color: "text-red-700", bg: "bg-red-50", border: "border-red-200", label: "Critique", banner: "bg-red-100" },
  warning: { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", label: "Attention", banner: "bg-amber-100" },
  info: { color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", label: "Info", banner: "bg-blue-100" },
};

export default function ErrorCodeDetail() {
  const { brand, code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/error-codes/${brand}/${code}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [brand, code]);

  useSEO({
    title: data
      ? `Code erreur ${data.code} ${data.brand} : ${data.title} — DoctoSpa`
      : "Code erreur spa — DoctoSpa",
    description: data
      ? `${data.title}. ${data.description.slice(0, 160)}`
      : "Détail d'un code erreur spa.",
    url: `${SITE}/codes-erreurs/${brand}/${code}`,
  });

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-24 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-24 text-center" data-testid="error-code-not-found">
        <h1 className="font-display text-3xl font-bold">Code introuvable</h1>
        <p className="mt-3 text-muted-foreground">Le code {code?.toUpperCase()} n'est pas répertorié pour {brand}.</p>
        <Button onClick={() => navigate("/codes-erreurs")} className="mt-6">
          <ArrowLeft className="h-4 w-4 mr-2" /> Retour à la liste
        </Button>
      </div>
    );
  }

  const s = SEVERITY_STYLES[data.severity] || SEVERITY_STYLES.warning;

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-14 lg:py-20" data-testid="error-code-detail-page">
      <Link
        to="/codes-erreurs"
        className="text-xs font-semibold text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        data-testid="back-to-codes"
      >
        <ArrowLeft className="h-3 w-3" /> Tous les codes erreurs
      </Link>

      <div className="mt-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge variant="outline" className="rounded-full uppercase text-[10px]">{data.brand}</Badge>
          <Badge variant="outline" className="rounded-full uppercase text-[10px]">{data.category}</Badge>
          <Badge variant="outline" className={`rounded-full text-[10px] ${s.color}`}>{s.label}</Badge>
        </div>
        <h1 className="mt-3 font-display text-4xl lg:text-5xl font-black tracking-tight" data-testid="code-title">
          Code <span className={`font-mono ${s.color}`}>{data.code}</span>
        </h1>
        <p className="mt-2 text-xl font-semibold text-secondary">{data.title}</p>
      </div>

      {/* Severity banner */}
      <Card className={`mt-8 p-5 border ${s.border} ${s.banner}`} data-testid="severity-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle className={`h-5 w-5 shrink-0 ${s.color}`} />
          <div>
            <div className={`font-bold ${s.color}`}>Sévérité : {s.label}</div>
            <div className="text-sm text-secondary mt-1">
              {data.severity === "critical" && "Ne pas utiliser le spa tant que le problème n'est pas résolu. Contactez un professionnel si besoin."}
              {data.severity === "warning" && "Le spa peut continuer à fonctionner mais nécessite votre attention."}
              {data.severity === "info" && "Comportement normal ou informatif. Aucune intervention immédiate requise."}
            </div>
          </div>
        </div>
      </Card>

      <div className="mt-8">
        <h2 className="font-display text-xl font-bold tracking-tight">Signification</h2>
        <p className="mt-3 text-muted-foreground leading-relaxed" data-testid="code-description">
          {data.description}
        </p>
      </div>

      <div className="mt-8">
        <h2 className="font-display text-xl font-bold tracking-tight">Que faire&nbsp;?</h2>
        <Card className="mt-3 p-5 bg-primary/5 border-primary/20" data-testid="code-actions">
          <p className="text-sm leading-relaxed">{data.action_summary}</p>
        </Card>
      </div>

      {/* Siblings */}
      {data.siblings && data.siblings.length > 0 && (
        <div className="mt-12" data-testid="code-siblings">
          <h2 className="font-display text-xl font-bold tracking-tight">Autres codes {data.brand}</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.siblings.map((c) => {
              const cs = SEVERITY_STYLES[c.severity] || SEVERITY_STYLES.warning;
              return (
                <Link
                  key={c.id}
                  to={`/codes-erreurs/${c.brand}/${c.code.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group"
                >
                  <Card className={`p-4 border ${cs.border} ${cs.bg} hover:shadow-md transition-all h-full cursor-pointer`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-mono font-black text-base ${cs.color}`}>{c.code}</span>
                          <Badge variant="outline" className={`text-[10px] rounded-full ${cs.color}`}>{cs.label}</Badge>
                        </div>
                        <div className="mt-1 text-sm font-semibold line-clamp-1">{c.title}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 opacity-0 group-hover:opacity-60 shrink-0 transition-opacity" />
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer CTA */}
      <div className="mt-16 rounded-2xl bg-secondary text-white p-8 lg:p-10">
        <h3 className="font-display text-2xl font-bold tracking-tight">Vous ne savez pas quoi faire ?</h3>
        <p className="mt-3 text-sm text-white/70 max-w-xl">
          Lancez un diagnostic guidé DoctoSpa pour obtenir un plan d'action détaillé, l'estimation de
          coût et les pièces suggérées pour votre spa.
        </p>
        <Link to="/diagnostic" className="mt-5 inline-block">
          <Button className="rounded-full h-11 px-6 bg-primary hover:bg-primary/90">
            Lancer un diagnostic
          </Button>
        </Link>
      </div>
    </div>
  );
}
